import {
  P2GTextField,
  P2GBox,
  P2GButton,
  P2GGrid,
  P2GText,
  SvgMagnify,
  P2GIcon,
  P2GAlert,
  P2GLink
} from '@parcel2go/core-ui';
import React, { useCallback, useState, useEffect, useMemo, Suspense, lazy } from 'react';
import useQuotes from '~/hooks/use-quotes';
import useDropShopProviders from '~/hooks/use-drop-shop-providers';
import { QuoteParcel, BasketQuote } from '~/lib/types/basket-types';
import { Shop } from '~/lib/types/edge-api-types';
import DropshopListCompact from '~/components/address-details/dropshops/dropshop-list-drawer';
import { isQuoteDropOff, quoteRequestParams } from '~/lib/quotes';
import { Label } from '~/lib/types/extra-types';
import { useShopSearch } from '~/hooks/use-shop-search';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { useSearchParams } from 'react-router-dom';

const LazyCmsContent = lazy(() => import('~/components/core/content/cms-content'));

type PostCodeSearchInputProps = {
  providerGroup: string;
  origin?: string;
  destination?: string;
  parcels?: QuoteParcel[];
  onQuotesFound?: (quotes: BasketQuote[]) => void;
  onError?: (error: Error) => void;
  onShopSelected?: (shop: Shop) => void;
};

export const PostCodeSearchInput = ({
  providerGroup,
  onQuotesFound,
  onError
}: Omit<PostCodeSearchInputProps, 'onShopSelected'>) => {
  const { settings } = useTenant();
  const { searchedPostcode, searchedShopProviders, setSearchedPostcode, setSearchedShopProviders } = useShopSearch();

  const [postcode, setPostcode] = useState<string>(searchedPostcode || '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchPostcode, setSearchPostcode] = useState<string | undefined>(searchedPostcode || undefined);
  const [visibleShopCount, setVisibleShopCount] = useState<number>(3);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);

  const quoteParams = useMemo(() => {
    return quoteRequestParams(searchParams, settings);
  }, [searchParams, settings]);

  useEffect(() => {
    if (quoteParams.originPostcode) {
      const trimmedPostcode = quoteParams.originPostcode.trim();

      if (trimmedPostcode && !searchedPostcode) {
        setPostcode(trimmedPostcode);
        setSearchPostcode(trimmedPostcode);
      }
    }
  }, [quoteParams.originPostcode, searchedPostcode]);

  const { quotes, loading, error } = useQuotes({ ...quoteParams, originPostcode: searchPostcode });

  const { shopProviders, loading: shopsLoading } = useDropShopProviders({
    postcode: searchPostcode,
    isoCode: quoteParams.origin
  });

  const dropoffQuotes = useMemo(() => quotes.filter(isQuoteDropOff), [quotes]);

  const allShops = useMemo(() => {
    const providers = searchPostcode ? shopProviders : searchedShopProviders;

    if (!providers) {
      return [];
    }

    const shopsList = providers[providerGroup];

    if (!shopsList) {
      return [];
    }

    return shopsList;
  }, [searchPostcode, shopProviders, providerGroup, searchedShopProviders]);

  const sortedShops = useMemo(() => [...allShops].sort((a, b) => a.distance - b.distance), [allShops]);

  const hasPrintInStore = useMemo(
    () =>
      dropoffQuotes.some((quote: BasketQuote) =>
        quote.availableExtras?.some((x: { key: string }) => x.key === Label.STORE)
      ),
    [dropoffQuotes]
  );

  const onSubmit = useCallback(() => {
    if (!postcode.trim()) return;

    const trimmedPostcode = postcode.trim();
    setIsLoading(true);
    setSearchPostcode(undefined);
    setVisibleShopCount(3);

    setTimeout(() => {
      setSearchPostcode(trimmedPostcode);
      setSearchedPostcode(trimmedPostcode);
    }, 0);
  }, [postcode, setSearchedPostcode]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        onSubmit();
      }
    },
    [onSubmit]
  );

  const handleShowMore = useCallback(() => {
    setVisibleShopCount((prev: number) => prev + 3);
  }, []);

  const displayedShops = useMemo(() => {
    return sortedShops.slice(0, visibleShopCount);
  }, [sortedShops, visibleShopCount]);

  const hasMoreShops = sortedShops.length > visibleShopCount;

  useEffect(() => {
    if (!searchPostcode) {
      setIsLoading(false);
      return;
    }

    setIsLoading(loading || shopsLoading);

    if (!loading && !shopsLoading) {
      setIsLoading(false);

      if (quotes.length > 0) {
        onQuotesFound?.(quotes);
      }

      if (error) {
        onError?.(error);
      }

      if (shopProviders && Object.keys(shopProviders).length > 0) {
        setSearchedShopProviders(shopProviders);
      }
    }
  }, [
    quotes,
    loading,
    error,
    searchPostcode,
    onQuotesFound,
    onError,
    shopsLoading,
    shopProviders,
    setSearchedShopProviders
  ]);

  return (
    <P2GBox>
      <P2GGrid container spacing={2}>
        <P2GGrid item xs={9}>
          <P2GTextField
            id="postcode-input"
            label={t('POSTCODE_INPUT_LABEL', { defaultValue: 'Postcode' })}
            placeholder={t('ENTER_POSTCODE_INPUT_PLACEHOLDER', { defaultValue: 'Enter Postcode' })}
            type="search"
            InputLabelProps={{ shrink: true }}
            value={postcode}
            fullWidth
            onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={handleKeyDown}
            loading={isLoading}
          />
        </P2GGrid>
        <P2GGrid item xs={3}>
          <P2GButton
            onClick={onSubmit}
            disabled={isLoading}
            variant="secondary"
            size="large"
            fullWidth
            sx={{
              height: '100%'
            }}
          >
            <P2GIcon size="xs">
              <SvgMagnify />
            </P2GIcon>
          </P2GButton>
        </P2GGrid>
      </P2GGrid>
      <Suspense>
        <LazyCmsContent contentKey="DRAWER-DROP-OFF-INPUT-ALERT">
          <P2GAlert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <P2GText variant="body2">
              {t('DRAWER_DROP_OFF_DESCRIPTION', {
                defaultValue: 'Distance to your nearest Drop off points will now display for all Drop off Services.'
              })}
            </P2GText>
          </P2GAlert>
        </LazyCmsContent>
      </Suspense>
      {(searchPostcode || searchedPostcode) && !isLoading && (
        <P2GBox mt={3}>
          {sortedShops.length > 0 ? (
            <>
              <DropshopListCompact shops={displayedShops} quoteOffersPrintInStore={hasPrintInStore} />
              {hasMoreShops && (
                <P2GBox mt={2}>
                  <P2GLink
                    onClick={handleShowMore}
                    variant="body1"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'none'
                      }
                    }}
                  >
                    {t('SHOW_MORE_DROP_OFFS_LINK', { defaultValue: 'Show More Locations' })}
                  </P2GLink>
                </P2GBox>
              )}
            </>
          ) : (
            <P2GText variant="body2" color="textSecondary">
              {quotes.length > 0
                ? t('NO_DROP_OFF_FOUND_ERROR', { defaultValue: 'No drop-off locations found for this postcode.' })
                : t('NO_SERVICES_FOUND_ERROR', { defaultValue: 'No services found for this postcode.' })}
            </P2GText>
          )}
        </P2GBox>
      )}
    </P2GBox>
  );
};
